(function() {
  const CUSTOM_SORT_KEYS = ['discount_percent', 'discount_amount', 'discount'];
  const MAX_PRODUCTS = 0;
  const CHUNK_SIZE = 20;
  const DEBUG = false;

  const COLLECTION_BASE_CLASS = 'discount-collection';

  const log = (...args) => DEBUG && console.log('[discount-sort]', ...args);
  const warn = (...args) => console.warn('[discount-sort]', ...args);
  const error = (...args) => console.error('[discount-sort]', ...args);

  function chunkArray(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  function getSearchParams() {
    const params = new URLSearchParams(window.location.search);
    log('Current URL params:', Object.fromEntries(params.entries()));
    return params;
  }

  function getCurrentSortBy(params) {
    const sortBy = params.get('sort_by');
    log('Detected sort_by:', sortBy);
    return sortBy;
  }

  function getCurrentPage(params) {
    const pageRaw = params.get('page') || '1';
    const page = parseInt(pageRaw, 10);
    log('Detected page:', pageRaw, '->', page);
    return page > 0 ? page : 1;
  }

  function setContainerState(
    container,
    { loading = false, loaded = false, errorState = false, empty = false, disabled = false } = {}
  ) {
    if (!container) return;

    const loadingClass = `${COLLECTION_BASE_CLASS}--loading`;
    const loadedClass = `${COLLECTION_BASE_CLASS}--loaded`;
    const errorClass = `${COLLECTION_BASE_CLASS}--error`;
    const emptyClass = `${COLLECTION_BASE_CLASS}--empty`;
    const disabledClass = `${COLLECTION_BASE_CLASS}--disabled`;

    container.classList.add(COLLECTION_BASE_CLASS);

    container.classList.remove(loadingClass, loadedClass, errorClass, emptyClass, disabledClass);

    if (loading) container.classList.add(loadingClass);
    if (loaded) container.classList.add(loadedClass);
    if (errorState) container.classList.add(errorClass);
    if (empty) container.classList.add(emptyClass);
    if (disabled) container.classList.add(disabledClass);

    log('setContainerState:', {
      loading: container.classList.contains(loadingClass),
      loaded: container.classList.contains(loadedClass),
      error: container.classList.contains(errorClass),
      empty: container.classList.contains(emptyClass),
      disabled: container.classList.contains(disabledClass),
      classList: Array.from(container.classList),
    });
  }

  function afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    if (typeof FoxTheme === 'undefined' || !FoxTheme || !FoxTheme.Motion) {
      return;
    }

    const items = container.querySelectorAll('.product-card');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);
  }

  const collectionCache = new Map();

  function buildCollectionCacheKey({ handle, filters }) {
    try {
      return JSON.stringify({ handle, filters });
    } catch (e) {
      warn('Failed to build cache key, caching disabled for this run', e);
      return null;
    }
  }

  async function getCollectionProducts({ endpoint, token, handle, filters }) {
    const cacheKey = buildCollectionCacheKey({ handle, filters });

    if (cacheKey) {
      const cached = collectionCache.get(cacheKey);
      if (cached) {
        if (cached.products) {
          log('Using cached products for collection', {
            cacheKey,
            count: cached.products.length,
          });
          return cached.products;
        }
        if (cached.promise) {
          log('Awaiting in-flight products fetch for collection', { cacheKey });
          return cached.promise;
        }
      }
    }

    const fetchPromise = fetchAllCollectionProducts({
      endpoint,
      token,
      handle,
      filters,
    })
      .then((products) => {
        if (cacheKey) {
          collectionCache.set(cacheKey, { products, promise: null });
        }
        return products;
      })
      .catch((err) => {
        if (cacheKey) {
          collectionCache.delete(cacheKey);
        }
        throw err;
      });

    if (cacheKey) {
      collectionCache.set(cacheKey, { products: null, promise: fetchPromise });
    }

    return fetchPromise;
  }

  function buildStorefrontFiltersFromUrl(params) {
    const filters = [];

    let priceMin = null;
    let priceMax = null;

    for (const [rawKey, rawValue] of params.entries()) {
      if (!rawKey.startsWith('filter.')) continue;
      if (!rawValue) continue;

      const parts = rawKey.split('.');
      const [, scope, attribute, ...attrScopeParts] = parts;
      const attrScope = attrScopeParts.length ? attrScopeParts.join('.') : null;

      const values = rawValue
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);

      if (!values.length) continue;

      if (attribute === 'price') {
        values.forEach((v) => {
          const num = parseFloat(v.replace(',', '.'));
          if (Number.isNaN(num)) return;

          if (attrScope === 'gte') {
            priceMin = priceMin == null ? num : Math.max(priceMin, num);
          } else if (attrScope === 'lte') {
            priceMax = priceMax == null ? num : Math.min(priceMax, num);
          }
        });
        log('Mapped', rawKey, '-> price boundary update', {
          priceMin,
          priceMax,
        });
        continue;
      }

      if (attribute === 'availability' && scope === 'v') {
        const has0 = values.includes('0');
        const has1 = values.includes('1');

        if (has0 && has1) {
          log('Availability 0,1 detected – skipping available filter');
        } else if (has1) {
          filters.push({ available: true });
          log('Mapped', rawKey, '-> available: true');
        } else if (has0) {
          filters.push({ available: false });
          log('Mapped', rawKey, '-> available: false');
        }
        continue;
      }

      values.forEach((value) => {
        switch (attribute) {
          case 'vendor':
            if (scope === 'p') {
              filters.push({ productVendor: value });
              log('Mapped', rawKey, '-> productVendor:', value);
            }
            break;

          case 'product_type':
            if (scope === 'p') {
              filters.push({ productType: value });
              log('Mapped', rawKey, '-> productType:', value);
            }
            break;

          case 'tag':
            if (scope === 'p') {
              filters.push({ tag: value });
              log('Mapped', rawKey, '-> tag:', value);
            }
            break;

          case 'option':
            if (scope === 'v' && attrScope) {
              const optionName = attrScope;
              filters.push({
                variantOption: {
                  name: optionName,
                  value,
                },
              });
              log('Mapped', rawKey, '-> variantOption:', {
                name: optionName,
                value,
              });
            }
            break;

          case 'm':
            if (!attrScope) break;
            {
              const [namespace, ...keyParts] = attrScope.split('.');
              const mfKey = keyParts.join('.');
              if (!namespace || !mfKey) break;

              if (scope === 'p') {
                filters.push({
                  productMetafield: {
                    namespace,
                    key: mfKey,
                    value,
                  },
                });
                log('Mapped', rawKey, '-> productMetafield:', {
                  namespace,
                  key: mfKey,
                  value,
                });
              } else if (scope === 'v') {
                filters.push({
                  variantMetafield: {
                    namespace,
                    key: mfKey,
                    value,
                  },
                });
                log('Mapped', rawKey, '-> variantMetafield:', {
                  namespace,
                  key: mfKey,
                  value,
                });
              }
            }
            break;

          case 't':
            if (!attrScope) break;

            if (scope === 'p' && attrScope === 'category') {
              value
                .split('__')
                .map((v) => v.trim())
                .filter(Boolean)
                .forEach((categoryId) => {
                  filters.push({ category: categoryId });
                  log('Mapped', rawKey, '-> category:', categoryId);
                });
            } else {
              const [tns, ...tKeyParts] = attrScope.split('.');
              const tKey = tKeyParts.join('.');
              if (!tns || !tKey) break;

              filters.push({
                taxonomyMetafield: {
                  namespace: tns,
                  key: tKey,
                  value,
                },
              });
              log('Mapped', rawKey, '-> taxonomyMetafield:', {
                namespace: tns,
                key: tKey,
                value,
              });
            }

            break;

          default:
            log('Ignoring unsupported filter param', {
              rawKey,
              scope,
              attribute,
              attrScope,
              value,
            });
        }
      });
    }

    if (priceMin != null || priceMax != null) {
      const priceFilter = {};
      if (priceMin != null) priceFilter.min = priceMin;
      if (priceMax != null) priceFilter.max = priceMax;

      filters.push({ price: priceFilter });
      log('Final price filter from URL:', priceFilter);
    }

    log('Final ProductFilter[] for Storefront API:', filters);
    return filters;
  }

  async function fetchAllCollectionProducts({ endpoint, token, handle, filters }) {
    let allProducts = [];
    let after = null;
    let hasNextPage = true;

    const EFFECTIVE_MAX_PRODUCTS =
      typeof MAX_PRODUCTS === 'number' && MAX_PRODUCTS > 0
        ? MAX_PRODUCTS
        : Number.POSITIVE_INFINITY;

    const FIRST_PER_REQUEST = 250;

    let totalRawCount = 0;
    let filteredOutTotal = 0;

    log('Start fetching products from Storefront API', {
      endpoint,
      handle,
      filters,
      MAX_PRODUCTS,
      effectiveMax: Number.isFinite(EFFECTIVE_MAX_PRODUCTS) ? EFFECTIVE_MAX_PRODUCTS : '∞',
      perRequest: FIRST_PER_REQUEST,
    });

    const query = `
      query CollectionProducts(
        $handle: String!,
        $first: Int!,
        $after: String,
        $filters: [ProductFilter!]
      ) {
        collection(handle: $handle) {
          products(first: $first, after: $after, filters: $filters) {
            edges {
              cursor
              node {
                id
                title
                handle
                onlineStoreUrl
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                compareAtPriceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                variants(first: 250) {
                  edges {
                    node {
                      id
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    `;

    let pageIndex = 0;

    while (hasNextPage && allProducts.length < EFFECTIVE_MAX_PRODUCTS) {
      pageIndex += 1;

      const remaining = EFFECTIVE_MAX_PRODUCTS - allProducts.length;
      const first = Number.isFinite(remaining)
        ? Math.min(FIRST_PER_REQUEST, remaining)
        : FIRST_PER_REQUEST;

      log(`Fetching Storefront page ${pageIndex}`, { after, first });

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query,
          variables: {
            handle,
            first,
            after,
            filters,
          },
        }),
      });

      if (!res.ok) {
        error('Storefront API HTTP error', res.status, res.statusText);
        break;
      }

      const json = await res.json();
      if (json.errors) {
        error('Storefront API GraphQL errors:', json.errors);
      }

      const products = json?.data?.collection?.products;
      if (!products) {
        warn('No products in Storefront API response for this collection');
        break;
      }

      const edges = products.edges || [];
      if (!edges.length) {
        log(`Storefront page ${pageIndex} returned 0 edges – stopping pagination`);
        break;
      }

      const nodes = edges.map((e) => e.node);

      totalRawCount += nodes.length;

      const visibleNodes = nodes.filter((p) => !!p.onlineStoreUrl);
      const filteredOnPage = nodes.length - visibleNodes.length;
      filteredOutTotal += filteredOnPage;

      if (filteredOnPage > 0) {
        log(
          `Page ${pageIndex}: filtered out ${filteredOnPage} products not published to Online Store (onlineStoreUrl == null)`
        );
      }

      allProducts = allProducts.concat(visibleNodes);

      log(`Fetched page ${pageIndex}:`, {
        edgesCount: edges.length,
        pageVisibleCount: visibleNodes.length,
        totalVisibleAccumulated: allProducts.length,
      });

      hasNextPage = Boolean(products.pageInfo?.hasNextPage && products.pageInfo?.endCursor);
      after = hasNextPage ? products.pageInfo.endCursor : null;

      if (!hasNextPage) {
        log('No more pages from Storefront API');
      }
      if (Number.isFinite(EFFECTIVE_MAX_PRODUCTS) && allProducts.length >= EFFECTIVE_MAX_PRODUCTS) {
        warn('Reached EFFECTIVE_MAX_PRODUCTS limit, stopping fetch loop');
      }
    }

    if (Number.isFinite(EFFECTIVE_MAX_PRODUCTS) && allProducts.length > EFFECTIVE_MAX_PRODUCTS) {
      allProducts = allProducts.slice(0, EFFECTIVE_MAX_PRODUCTS);
    }

    log('Finished fetching products from Storefront API', {
      totalRaw: totalRawCount,
      totalVisible: allProducts.length,
      filteredOutTotal,
    });

    return allProducts;
  }

  function getFirstAvailableVariant(product) {
    const edges = product?.variants?.edges;
    if (!Array.isArray(edges) || !edges.length) return null;

    const availableEdge = edges.find((edge) => edge?.node?.availableForSale);
    if (availableEdge && availableEdge.node) {
      return availableEdge.node;
    }

    return edges[0]?.node || null;
  }

  function computeDiscount(product) {
    const variant = getFirstAvailableVariant(product);

    let priceRaw;
    let compareRaw;
    let source = 'priceRange';

    if (variant && variant.price) {
      priceRaw = variant.price.amount;
      compareRaw = variant.compareAtPrice ? variant.compareAtPrice.amount : null;
      source = 'variant';
    } else {
      priceRaw = product?.priceRange?.minVariantPrice?.amount;
      compareRaw = product?.compareAtPriceRange?.minVariantPrice?.amount;
      source = 'priceRange';
    }

    const price = priceRaw != null ? Number(priceRaw) : 0;
    const compareAt = compareRaw != null ? Number(compareRaw) : 0;

    if (!compareAt || compareAt <= price) {
      return {
        amount: 0,
        percent: 0,
        hasDiscount: false,
        price,
        compareAt,
        fromVariant: source === 'variant',
        variantId: variant?.id || null,
      };
    }

    const amount = compareAt - price;
    const percent = (amount / compareAt) * 100;

    return {
      amount,
      percent,
      hasDiscount: amount > 0 && percent > 0,
      price,
      compareAt,
      fromVariant: source === 'variant',
      variantId: variant?.id || null,
    };
  }

  function sortProductsByDiscount(products, sortKey) {
    const key = sortKey === 'discount' ? 'discount_percent' : sortKey;
    log('Sorting products by discount, mode:', key, 'Total products:', products.length);

    const enriched = products.map((p, index) => {
      const d = computeDiscount(p);
      return {
        product: p,
        discount: d,
        originalIndex: index,
      };
    });

    enriched.sort((a, b) => {
      const da = a.discount;
      const db = b.discount;

      const aHas = da.hasDiscount;
      const bHas = db.hasDiscount;

      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;

      if (!aHas && !bHas) {
        return a.originalIndex - b.originalIndex;
      }

      if (key === 'discount_percent') {
        if (db.percent !== da.percent) {
          return db.percent - da.percent;
        }
        if (db.amount !== da.amount) {
          return db.amount - da.amount;
        }
      } else if (key === 'discount_amount') {
        if (db.amount !== da.amount) {
          return db.amount - da.amount;
        }
        if (db.percent !== da.percent) {
          return db.percent - da.percent;
        }
      }

      return a.originalIndex - b.originalIndex;
    });

    const sorted = enriched.map((e) => e.product);

    if (!DEBUG) {
      return sorted;
    }

    const debugList = enriched.slice(0, 250).map((entry, idx) => {
      const { product, discount } = entry;
      return {
        pos: idx + 1,
        handle: product.handle,
        title: product.title,
        price: discount.price,
        compareAt: discount.compareAt,
        discountAmount: Number(discount.amount.toFixed(2)),
        discountPercent: Number(discount.percent.toFixed(2)),
        hasDiscount: discount.hasDiscount,
        originalIndex: entry.originalIndex,
        source: discount.fromVariant ? 'first_available_variant' : 'priceRange',
        variantId: discount.variantId || null,
      };
    });

    log('Full sorted products list with discounts (first 250):', debugList);

    const noDiscountDebug = debugList.filter((row) => !row.hasDiscount).slice(0, 10);
    if (noDiscountDebug.length) {
      log('First products WITHOUT discount (after sorting):', noDiscountDebug);
    }

    return sorted;
  }

  async function renderCollectionByHandles({ container, sectionId, handles }) {
    log('renderCollectionByHandles() called', {
      sectionId,
      handlesCount: handles.length,
    });

    if (!handles.length) {
      warn('renderCollectionByHandles: no handles provided');
      return;
    }

    const chunks = chunkArray(handles, CHUNK_SIZE);
    log(
      `Split ${handles.length} handles into ${chunks.length} chunks of up to ${CHUNK_SIZE}`
    );

    const baseUrl = new URL(window.location.href);
    baseUrl.searchParams.set('section_id', sectionId);
    baseUrl.searchParams.delete('products');

    const requests = chunks.map((chunkHandles, index) => {
      const url = new URL(baseUrl.toString());
      url.searchParams.set('products', chunkHandles.join(','));

      log(`Chunk ${index + 1}/${chunks.length} URL:`, url.toString());

      return fetch(url.toString())
        .then((res) => {
          if (!res.ok) {
            throw new Error(
              `Section render HTTP ${res.status} for chunk ${index + 1}`
            );
          }
          return res.text();
        })
        .then((html) => ({ index, html }))
        .catch((err) => {
          error('Error fetching chunk', index + 1, err);
          return null;
        });
    });

    const rawResults = await Promise.all(requests);
    const results = rawResults.filter(Boolean);

    if (!results.length) {
      const msg = 'renderCollectionByHandles: no successful chunk responses – aborting';
      warn(msg);
      throw new Error(msg);
    }

    results.sort((a, b) => a.index - b.index);

    const parser = new DOMParser();

    let liveProductsList = container.querySelector('#ProductsList');
    if (!liveProductsList) {
      warn(
        'renderCollectionByHandles: no #ProductsList inside container – falling back to container as root'
      );
    }

    const targetRoot = liveProductsList || container;

    let targetGrid =
      targetRoot.querySelector('.grid') ||
      targetRoot.querySelector('[data-products-grid]') ||
      targetRoot.querySelector('.collection__grid') ||
      targetRoot;

    log('renderCollectionByHandles: targetGrid element:', targetGrid);

    targetGrid.querySelectorAll('.f-column').forEach((item) => item.remove());

    const fragment = document.createDocumentFragment();

    results.forEach(({ index, html }) => {
      const doc = parser.parseFromString(html, 'text/html');

      const chunkContainer = doc.querySelector('#ProductGridContainer') || doc;
      const chunkProductsList =
        chunkContainer.querySelector('#ProductsList') || chunkContainer;

      const chunkGrid =
        chunkProductsList.querySelector('.grid') ||
        chunkProductsList.querySelector('[data-products-grid]') ||
        chunkProductsList.querySelector('.collection__grid') ||
        chunkProductsList;

      const items = chunkGrid.querySelectorAll('.f-column');

      log(
        `Chunk ${index + 1}/${results.length}: appending ${items.length} .f-column elements`
      );

      items.forEach((item) => {
        fragment.appendChild(item);
      });
    });

    targetGrid.appendChild(fragment);

    log(
      'renderCollectionByHandles: finished updating grid items inside existing #ProductsList'
    );
  }

  let isApplyInProgress = false;

  async function applyDiscountSort() {
    if (isApplyInProgress) {
      log('applyDiscountSort(): skip – already in progress');
      return;
    }
    isApplyInProgress = true;

    log('applyDiscountSort() called');

    const container = document.querySelector('#ProductGridContainer[data-discount-sort-root="true"]');
    if (!container) {
      warn('No #ProductGridContainer with data-discount-sort-root found – aborting');
      isApplyInProgress = false;
      return;
    }

    const params = getSearchParams();
    const sortBy = getCurrentSortBy(params);

    if (!CUSTOM_SORT_KEYS.includes(sortBy)) {
      log('sort_by is not custom discount sort. Skipping custom logic.', { sortBy });
      setContainerState(container, { disabled: true });
      isApplyInProgress = false;
      return;
    }

    log('Custom discount sorting is active', { sortBy });

    const handle = container.dataset.collectionHandle;
    const sectionId = container.dataset.sectionId;
    const perPage = parseInt(container.dataset.productsPerPage || '24', 10);
    const endpoint = container.dataset.storefrontEndpoint;
    const token = container.dataset.storefrontToken;

    log('Container dataset config:', {
      handle,
      sectionId,
      perPage,
      endpoint,
      hasToken: !!token,
    });

    if (!handle || !sectionId || !endpoint || !token) {
      warn('Missing handle/sectionId/endpoint/token on #ProductGridContainer – aborting custom sort');
      isApplyInProgress = false;
      return;
    }

    const page = getCurrentPage(params);
    const filters = buildStorefrontFiltersFromUrl(params);

    let hadError = false;
    let hadEmpty = false;
    setContainerState(container, { loading: true });

    try {
      const products = await getCollectionProducts({
        endpoint,
        token,
        handle,
        filters,
      });

      if (!products.length) {
        warn('No products returned from Storefront API – skipping replace');
        hadEmpty = true;
        return;
      }

      const sorted = sortProductsByDiscount(products, sortBy);

      const start = (page - 1) * perPage;
      const slice = sorted.slice(start, start + perPage);
      const handles = slice.map((p) => p.handle).filter(Boolean);

      log('Paging / slicing info:', {
        page,
        perPage,
        startIndex: start,
        endIndex: start + perPage,
        sliceLength: slice.length,
        handles,
      });

      if (!handles.length) {
        warn('No handles after slicing – nothing to render');
        hadEmpty = true;
        return;
      }

      await renderCollectionByHandles({
        container,
        sectionId,
        handles,
      });

      log('applyDiscountSort(): finished render');
    } catch (e) {
      hadError = true;
      error('Failed to apply discount sort', e);
    } finally {
      const updated = document.querySelector('#ProductGridContainer[data-discount-sort-root="true"]');
      if (updated) {
        if (hadError) {
          setContainerState(updated, { errorState: true });
        } else if (hadEmpty) {
          setContainerState(updated, { empty: true });
        } else {
          setContainerState(updated, { loaded: true });
        }
        afterRenderSection();
      }
      log('applyDiscountSort() finished');
      isApplyInProgress = false;
    }
  }

  function initDiscountSort() {
    log('Initializing discount-sort');

    document.addEventListener('DOMContentLoaded', () => {
      log('DOMContentLoaded – running initial applyDiscountSort()');
      applyDiscountSort();
    });

    let rerenderTimeout = null;
    document.addEventListener('collection:rerendered', () => {
      log('Event "collection:rerendered" caught – reapplying discount sort');
      if (rerenderTimeout) {
        clearTimeout(rerenderTimeout);
      }
      rerenderTimeout = setTimeout(() => {
        applyDiscountSort();
      }, 50);
    });
  }

  initDiscountSort();
})();
